import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { api, type User } from '../api/client';

/** 认证上下文值 */
interface AuthContextValue {
  /** 当前登录用户，未登录为 null */
  user: User | null;
  /** 启动校验 token 期间的加载态 */
  loading: boolean;
  /** 登录 */
  login: (email: string, password: string) => Promise<void>;
  /** 注册 */
  register: (email: string, password: string, name: string) => Promise<void>;
  /** 退出登录，清除本地凭证 */
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'cloupak_token';
const USER_KEY = 'cloupak_user';

/** 从 localStorage 读取已保存的 user（解析失败时清除脏数据） */
function readStoredUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

/**
 * 认证 Provider：
 * - 启动时从 localStorage 同步恢复 user，避免首屏闪烁
 * - 若存在 token，后台静默调用 getMe 校验并刷新 user
 * - login / register 成功后持久化 token 与 user
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // 同步恢复 user，避免首屏闪烁
  const [user, setUser] = useState<User | null>(readStoredUser);
  // 存在 token 时进入 loading，等待 getMe 校验完成
  const [loading, setLoading] = useState<boolean>(
    () => localStorage.getItem(TOKEN_KEY) !== null,
  );

  // 启动时若存在 token，静默调用 getMe 校验并刷新 user
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .getMe()
      .then((fresh) => {
        if (cancelled) return;
        setUser(fresh);
        localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      })
      .catch(() => {
        if (cancelled) return;
        // token 无效，清理本地状态
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function login(email: string, password: string) {
    const { token, user: u } = await api.login(email, password);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
  }

  async function register(email: string, password: string, name: string) {
    const { token, user: u } = await api.register(email, password, name);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setUser(u);
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

/** 在组件中获取认证状态与方法，必须在 AuthProvider 内部使用 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用');
  }
  return ctx;
}
